// CGIAR Quick Style Guide v4.0 — injected into every LLM prompt as a <style-guide> XML tag.
// Source: style_guide/CGIAR_Quick_Style_Guide.txt
// Update this constant when the style guide is revised.

export const CGIAR_STYLE_GUIDE = `CGIAR QUICK STYLE GUIDE
Version 4.0 | Effective February 2024

GENERAL OBJECTIVE

This style guide is intended to ensure consistency across CGIAR written products
by providing guidance on spelling, punctuation, formatting, and other matters of
style.

PRIMARY REFERENCES

The primary references for CGIAR style are The Chicago Manual of Style (CMS) and
Merriam-Webster's Collegiate Dictionary. American spelling and punctuation (e.g.,
commas and full stops/periods inside quotation marks) is preferred.

ACRONYMS AND OTHER ABBREVIATIONS

Spell out acronyms and other abbreviations on first use and use only the acronym
thereafter, when they are used frequently. Some organization names are never
spelled out, including CGIAR. Very common abbreviations do not need spelling out:
GDP, IT, M&E, NGO, R&D, and UN.

When spelling out abbreviations that do not stand for proper names, the spelled-out
version should not be capitalized (e.g., gross domestic product [GDP]).

Units of measure should be abbreviated when following a number (3 ha, 45 km) but
not otherwise. Abbreviate metric tons as "t," not "mt" or "MT."

CAPITALIZATION

- Proper nouns, including names of places, institutions, and brands: use title case.
- Program and project names: use title case; do not italicize or put in quotation marks.
- People's titles: capitalize ONLY when they precede the person's name.
- Names of institutions and government bodies: capitalize ONLY when referring to a particular entity.

TITLES, HEADLINES, AND SUBTITLES

Use sentence case in all titles, headlines, and subtitles.

PUNCTUATION

Serial (Oxford) comma: Use a serial comma in a series of three or more words or
phrases. Example: apples, oranges, and lemons.

Semicolons in lists: Use semicolons in lists that include internal punctuation.

Bulleted lists:
- Always use a colon between the introductory clause and the list that follows.
- Capitalize each item.
- Use a period after each item if it is a complete sentence.
- Do not use semicolons in bulleted lists.

WRITING ABOUT CGIAR

- CGIAR is never spelled out and does not take "the" when it stands alone.
- Centers that are part of One CGIAR: refer to as "CGIAR Research Centers."
- CGIAR Research Initiative names: use title case (acronyms are all caps).
- Always capitalize: System Board, System Council, Executive Management Team, Senior Leadership Team.

COMPOUND WORDS

- Compound adjectives should generally be hyphenated (long-term plan, small-scale farmers).
- Do not hyphenate directional words except in a proper name (southwest, northeast).
- Compounds formed with prefixes and suffixes are normally closed (no hyphen).

Prefix-specific rules:
  agri/agro: agribusiness, agrifood, agritech, agroecology, agroforestry
  multi: multicountry, multidisciplinary, multisectoral, multiyear
  post: postdoctoral, postharvest, postfarm, postwar
  pre: preindustrial, premodern, preregistration, prewar
  wide: economywide, regionwide

NON-ENGLISH WORDS

Do NOT italicize non-English words commonly used in English (a priori, e.g., et al.,
etc., ex post, ex ante, i.e., in situ, de facto) or names of currencies, institutions,
places, or programs.

AVOIDING DISCRIMINATORY LANGUAGE

- Avoid sexist, racist, homophobic, disablist, or other biased language.
- Use: developed and developing countries; high-, low-, and middle-income countries;
  chairperson; fisher; work hours; disability; people with disabilities; they/their.
- Avoid: First World/Third World; chairman; fisherman; man hours; handicap; the disabled.
- Frame agency as belonging to the people themselves (avoid "lifting people out of poverty").
- Use "they/their" for plural or unspecified gender references.

GENDER TERMINOLOGY

- Gender = socially constructed relationships/differences between men and women.
- Sex = biological differences between males and females.
- "Gender empowerment" is INCORRECT. Use "women's empowerment" or "gender equality/equity."
- Do NOT use "male" and "female" as nouns to refer to people.
- USE: women farmers / men farmers. AVOID: female farmers / male farmers.

COUNTRY NAMES

CGIAR follows United Nations guidance on country names.
Specific names: Cote d'Ivoire (not Ivory Coast), Turkiye (not Turkey),
Viet Nam (not Vietnam).

SPECIFIC WORD PREFERENCES

USE (preferred):             AVOID:
afterward, toward            afterwards, towards
agrifood                     agri-food
amid                         amidst
among                        amongst
because                      as/since/while (for causal relationships)
compared with                compared to (except for similarities)
in contrast                  by contrast
data are (always plural)     data is
different from               different than
internet                     Internet
more than / less than        over/under (for quantities)
per year                     per annum
to                           in order to
use                          utilize
while                        whilst
global South                 Global South

NUMBERS, DATES, MEASUREMENTS, AND CURRENCIES

- Spell out whole numbers from zero to nine; use numerals for 10 and above.
- When numbers below 10 appear alongside numbers above 10 in the same sentence, use numerals for all.
- Write large numbers as: 10 million, 45 billion (not 5 thousand).
- Numbers greater than 999 use commas to mark thousands.

Dates: Use European style — 1 January 2012 (not January 1, 2012).
Decades: 1960s and 1970s (not 1960's or sixties).
Number ranges: use an en dash; include all four digits of both years (1999-2004).

UNITS OF MEASURE

- Use metric (SI) measures wherever possible.
- Use US dollars where possible; write as USD $200.
- Percentages in body text: spell out "percent" (4 percent, not 4%).
- In tables and figures: use the "%" symbol.
- USE "percent" with a number: 47 percent. USE "percentage" as a noun: the percentage of...`;
